import { Inject } from '@odinjs/odin';

import { Endpoint, FormViewService, ProviderAccreditationPendenciesService, ToastServiceProviderAccreditation } from '@philips-emr/hpms-provider-accreditation-commons';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent, HPMSOnCreateOption, HPMSOnOptionClick } from '@philips-emr/hpms-provider-accreditation-components';
import { FieldAPI, FieldListAPI, FieldListValue, FormAPI, PanelAPI, RadioGroupAPI, RadioGroupItem, WizardAPI } from '@philips-emr/tws-components';
import { ActionAPI, FetchAPI, InputAPI, LinkAPI, LocatorAPI, ToastNotificationAPI, UnboxedAutocompleteAPI } from '@philips-emr/tws-components/lib/components';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnAfterFetch, OnCancel, OnChange, OnClick, OnClose, OnMount, OnSubmit, OnValidate } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, ClickEvent, FetchEvent, MountEvent, SubmitEvent, ValidateEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { ProviderAccreditationPackagesAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

import { AccreditationSteps } from './provider-accreditation-controller';

interface Package {
  vlPackage: number,

  mainProcedure: LocatorProcedure,
  packageType: RadioGroupItem,
  dsMainProcedure: string,
  nrSeqPackage: number,
  ieComposition: string,
  editable: string,
  isDetails: boolean,
}

interface Item {
  ieItemType: string,
  amount: string,
  dsItem: string,
  locatorProcedure: LocatorProcedure,
  medicineMaterialLocator: LocatorMatMed,
  nrSeqItem: number,
  nrSeqPackage: number,
  dsMainProcedure: string,
  ieComposition: string,
  dsAction: string,
}

interface LocatorProcedure {
  cdProcedure: number,
  dsProcedure: string,
  ieSourceProcedure: number,
}

interface LocatorMatMed {
  cdMaterial: number,
  dsMaterial: string,
  nrSeqMaterialLocator: number,
}

enum EDITABLE {
  NO = 'N',
  YES = 'S',
}

const OptionKey = {
  EDIT: 'edit',
  DELETE: 'delete',
  DETAIL: 'detail',
};

const PROVIDER = 'P';
const HEALTH_INSURANCE = 'O';
const PROCEDURE = 'P';

const buttomWritePackage = {
  key: PROVIDER,
  label: MessageService.translate('pa.write-package'),
} as RadioGroupItem;
const buttomRequestPackage = {
  key: HEALTH_INSURANCE,
  label: MessageService.translate('pa.request-composition'),
} as RadioGroupItem;

@Controller({ fragment: FragmentNames.PROVIDER_ACCREDITATION_PACKAGES })
class ProviderAccreditationPackagesController extends BaseController {

  @Inject(Aliases.PANEL_ITEM_LIST_EDIT)
  private panelItemListEdit: PanelAPI;
  @Inject(Aliases.FIELD_ITEM_LIST_EDIT)
  private itemListEdit: FieldListAPI;
  @Inject(Aliases.PANEL_MODAL)
  private panelModal: PanelAPI;
  @Inject(Aliases.FORM_MAIN_PROCEDURE)
  private formMainProcedure: FormAPI;
  @Inject(Aliases.FETCH_PACKAGES)
  private fetchPackages: FetchAPI;
  @Inject(Aliases.FIELD_ITEM_LIST)
  private fieldItemList: FieldListAPI;
  @Inject(Aliases.WIZARD_PACKAGE)
  private wizardPackage: WizardAPI;
  @Inject(Aliases.PANEL_EDIT)
  private panelEdit: PanelAPI;
  @Inject(Aliases.FIELD_PROCEDURE_EDIT)
  private fieldProcedureEdit: FieldAPI;
  @Inject(Aliases.FIELD_MEDICINE_MATERIAL_EDIT)
  private fieldMedicineMaterialEdit: FieldAPI;
  @Inject(Aliases.LOCATOR_PROCEDURE_EDIT)
  private locatorProcedureEdit: LocatorAPI;
  @Inject(Aliases.FORM_ITEM_EDIT)
  private formItemEdit: FormAPI;
  @Inject(Aliases.FORM_ITEM)
  private formItem: FormAPI;
  @Inject(Aliases.RADIO_GROUP_PACKAGE_TYPE_EDIT)
  private radioGroupPackageTypeEdit: RadioGroupAPI;
  @Inject(Aliases.RADIO_GROUP_PACKAGE_TYPE)
  private radioGroupPackageType: RadioGroupAPI;
  @Inject(Aliases.FIELD_PROCEDURE)
  private fieldProcedure: FieldAPI;
  @Inject(Aliases.LOCATOR_PROCEDURE)
  private locatorProcedure: LocatorAPI;
  @Inject(Aliases.FIELD_MEDICINE_MATERIAL)
  private fieldMedicineMaterial: FieldAPI;
  @Inject(Aliases.FORM_EDIT_PACKAGE)
  private formEditPackage: FormAPI;
  @Inject(Aliases.PANEL_FORM_ITEM_EDIT)
  private panelFormItemEdit: PanelAPI;
  @Inject(Aliases.PANEL_LINK_ADD_ITEMS)
  private panelLinkAddItems: PanelAPI;
  @Inject(Aliases.INPUT_VALUE)
  private inputValue: InputAPI;
  @Inject(Aliases.AUTOCOMPLETE_TYPE_EDIT)
  private unboxedAutocompleteEdit: UnboxedAutocompleteAPI;
  @Inject(Aliases.AUTOCOMPLETE_TYPE)
  private unboxedAutocompleteType: UnboxedAutocompleteAPI;
  @Inject(Aliases.FIELD_ITEM_LIST_EDIT)
  private fieldiItemListEdit: FieldListAPI;
  @Inject(Aliases.PANEL_TITLE)
  private panelTitle: PanelAPI;
  @Inject(Aliases.PANEL_PENDENCIES)
  private panelPendencies: PanelAPI;
  @Inject(Aliases.TOAST_NOTIFICATION_REVISION)
  private toastNotificationRevision: ToastNotificationAPI;
  @Inject(Aliases.PANEL_LOADER)
  private panelLoader: PanelAPI;

  @OnMount([Aliases.RADIO_GROUP_PACKAGE_TYPE, Aliases.RADIO_GROUP_PACKAGE_TYPE_EDIT])
  onMountRadio(event: MountEvent): void {
    const api = event.target as RadioGroupAPI;
    const { nrSeqUser } = this.historyService.getCurrentRouteParams();
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const { nrSeqPackage, ieComposition, isDetails } = this.context.getBulkData();
    this.context.set({ nrSeqUser });
    api.setData([buttomWritePackage, buttomRequestPackage]);
    api.setValue(buttomWritePackage);

    if (api.getAlias() == Aliases.RADIO_GROUP_PACKAGE_TYPE_EDIT && (ieComposition == PROVIDER || isDetails)) {
      this.panelItemListEdit.setVisible(true);
      this.getPackageItems(nrSeqAccreditation as number, nrSeqPackage as number);
    }
  }

  @OnMount([Aliases.FIELD_ITEM_LIST, Aliases.FIELD_ITEM_LIST_EDIT])
  OnMountList(event: MountEvent): void {
    const api = event.target as FieldListAPI;
    api.setValue([]);
  }

  @OnMount(Aliases.FETCH_PENDENCIES)
  onMountFetchPendencies(event: MountEvent): void {
    ProviderAccreditationPendenciesService.fetchCardPendencies(event, AccreditationSteps.PACKAGES);
  }

  @OnAfterFetch(Aliases.FETCH_PENDENCIES)
  onAfterFetch(event: FetchEvent): void {
    if (isEmptyArrayOrObject(event.data)) {
      this.panelPendencies.setVisible(false);
    } else {
      ProviderAccreditationPendenciesService.setMessageToastPendency(this.toastNotificationRevision, event);
    }
  }

  @OnChange(Aliases.FIELD_ITEM_LIST_EDIT)
  public onChangeItemListEdit(event: ChangeEvent<FieldListValue>): void {
    this.fieldiItemListEdit.setValue(event.value);
  }

  @OnCancel(Aliases.FORM_EDIT_PACKAGE)
  onCancelEdit(): void {
    this.panelEdit.setVisible(false);
  }

  @OnMount(Aliases.FETCH_PACKAGES)
  onMountFetchPackages(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    api.setParameters({ nrSeqAccreditation });
    api.forceFetch();
  }

  @OnMount([
    Aliases.FIELD_MEDICINE_MATERIAL,
    Aliases.FIELD_MEDICINE_MATERIAL_EDIT,
  ])
  onMountFields(event: MountEvent): void {
    const api = event.target as FieldAPI;
    api.setVisible(false);
  }

  @OnClick(Aliases.ACTION_ADD)
  onClickAction(): void {
    this.panelModal.setVisible(true);
  }

  @OnMount(Aliases.ACTION_ADD)
  onMountActionAdd(event: MountEvent): void {
    const api = event.target as ActionAPI;
    FormViewService.shouldRenderAction(api, AccreditationSteps.PACKAGES);
  }

  @OnSubmit(Aliases.WIZARD_PACKAGE)
  onSubmitWizard(): void {
    const record = this.formMainProcedure.getRecord() as unknown as Package;
    const { packageType } = record;
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    let items;

    if (!packageType) {
      record.packageType = buttomWritePackage;
    }

    if (record.packageType.key != HEALTH_INSURANCE) {
      items = this.fieldItemList.getValue();
    }

    const packageTypeKey = record.packageType.key;
    const radioGroupPackageTypeValue = this.radioGroupPackageType.getValue()?.key;

    if (packageTypeKey === PROVIDER && radioGroupPackageTypeValue === PROVIDER) {
      const type = this.unboxedAutocompleteType.getValue();
      const formComposition = this.formItem.getRecord() as unknown as Item;
      const locatorProcedureIsEmpty = isEmptyArrayOrObject(formComposition.locatorProcedure);
      const medicineMaterialLocatorIsEmpty = isEmptyArrayOrObject(formComposition.medicineMaterialLocator);

      if (!locatorProcedureIsEmpty || !medicineMaterialLocatorIsEmpty) {
        const dsItem = type === PROCEDURE ? formComposition.locatorProcedure.dsProcedure : formComposition.medicineMaterialLocator.dsMaterial;

        if (dsItem) {
          const message = MessageService.translate('pa.package-item-warning').replace('#@DS_ITEM#@', dsItem);
          ToastServiceProviderAccreditation.error(message);
          return;
        }
      }
    }

    const body = {
      mainProcedure: record.mainProcedure.cdProcedure,
      ieSourceProcedure: record.mainProcedure.ieSourceProcedure,
      vlPackage: record.vlPackage,
      composition: record.packageType.key,
      items,
    };

    HttpService.post<Record<string, Value>>(Endpoint.POST.PACKAGE, { nrSeqAccreditation }, { body })
      .then(() => {
        this.panelModal.setVisible(false);
        this.fetchPackages.setParameters({ nrSeqAccreditation });
        this.fetchPackages.forceFetch();

      }).catch((error) => {
        ToastServiceProviderAccreditation.error(error.body);
      });
  }

  @OnChange(Aliases.RADIO_GROUP_PACKAGE_TYPE)
  onChangeRadioButton(event: ChangeEvent<{ key: string }>): void {
    if (event.value.key == HEALTH_INSURANCE) {
      this.wizardPackage.setHiddenSteps(['packageComposition']);
      return;
    }
    this.wizardPackage.setHiddenSteps([]);
  }

  @OnChange(Aliases.FIELD_RADIO_BUTTON_EDIT)
  onChangeRadioButtonEdit(event: ChangeEvent<{ key: string }>): void {
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const { nrSeqPackage } = this.context.getBulkData();

    if (event.value.key == PROVIDER) {
      this.panelItemListEdit.setVisible(true);
      this.getPackageItems(nrSeqAccreditation as number, nrSeqPackage as number);
      this.panelTitle.setVisible(true);
      return;
    }
    this.panelItemListEdit.setVisible(false);
    this.panelTitle.setVisible(false);
  }

  @OnValidate(Aliases.FORM_MAIN_PROCEDURE)
  onValidatePersonalInformationForm(event: ValidateEvent): void {
    const { valid } = event;
    this.wizardPackage.setStepDone('main-package', valid);
  }

  @OnChange(Aliases.FIELD_ITEM_LIST)
  public onChangeItemList(event: ChangeEvent<FieldListValue>): void {
    this.fieldItemList.setValue(event.value);
    const value = [...this.fieldItemList.getValue()];
    if (value.length > 0) {
      this.wizardPackage.setStepDone('packageComposition', true);
      return;
    }
    this.wizardPackage.setStepDone('packageComposition', false);

  }

  @HPMSOnCreateOption(Aliases.CARD_PROCEDURE)
  onCreateOption(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, key, shouldRender } = event;
    const { editable } = data;

    const show = {
      [OptionKey.DETAIL]: editable == EDITABLE.NO,
      [OptionKey.EDIT]: editable == EDITABLE.YES,
      [OptionKey.DELETE]: editable == EDITABLE.YES,
    };
    shouldRender(show[key]);
  }

  @HPMSOnOptionClick(Aliases.CARD_PROCEDURE)
  onOptionClick(event: HpmsCustomCardEvent<Item>): void {
    const { data, key } = event;
    const { nrSeqPackage, ieComposition } = data;
    const action = {
      [OptionKey.DETAIL]: () => this.onDetails(nrSeqPackage, ieComposition),
      [OptionKey.DELETE]: () => this.deletePackage(nrSeqPackage),
      [OptionKey.EDIT]: () => this.onEdit(nrSeqPackage, ieComposition),
    };
    if (action[key]) {
      action[key]();
    }
  }

  @OnClose(Aliases.MODAL_ADD_PACKAGE)
  onCloseModal(): void {
    this.panelModal.setVisible(false);
  }

  @OnClose(Aliases.MODAL_EDIT)
  onCloseModalEdit(): void {
    this.panelEdit.setVisible(false);
  }

  @OnMount([Aliases.FETCH_EDIT_PACKAGE, Aliases.FETCH_AUTOCOMPLETE_EDIT])
  onMountFetchEdit(event: MountEvent): void {
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const { nrSeqPackage } = this.context.getBulkData();
    this.context.set({ nrSeqAccreditation });
    const api = event.target as FetchAPI;
    api.setParameters({ nrSeqAccreditation, nrSeqPackage });
    api.forceFetch();
  }

  @OnMount(Aliases.AUTOCOMPLETE_TYPE_EDIT)
  onMountAutocompleteEdit(event: MountEvent): void {
    const api = event.target as UnboxedAutocompleteAPI;
    api.setValue(PROCEDURE);
  }

  @OnChange(Aliases.FIELD_AUTOCOMPLETE_EDIT)
  onChangeAutocompleteEdit(event: ChangeEvent<{ ieItemType: string }>): void {
    const ieItemType = event.value as unknown as string;

    if (ieItemType == PROVIDER) {
      this.fieldProcedureEdit.setVisible(true);
      this.fieldMedicineMaterialEdit.setVisible(false);
      this.locatorProcedureEdit.setEnabled(true);
      return;
    }
    this.fieldProcedureEdit.setVisible(false);
    this.fieldMedicineMaterialEdit.setVisible(true);
  }

  @OnMount(Aliases.AUTOCOMPLETE_TYPE)
  onMountAutocomplete(event: MountEvent): void {
    const api = event.target as UnboxedAutocompleteAPI;
    api.setValue(PROCEDURE);
  }

  @OnChange(Aliases.FIELD_AUTOCOMPLETE)
  onChangeAutocomplete(event: ChangeEvent<{ ieItemType: string }>): void {
    const ieItemType = event.value as unknown as string;

    if (ieItemType == PROVIDER) {
      this.fieldProcedure.setVisible(true);
      this.fieldMedicineMaterial.setVisible(false);
      this.locatorProcedure.setEnabled(true);
      return;
    }
    this.fieldProcedure.setVisible(false);
    this.fieldMedicineMaterial.setVisible(true);
  }

  @OnClick([Aliases.LINK_ADD, Aliases.LINK_ADD_ITEM])
  onSaveActionClick(event: ClickEvent): void {
    const api = event.target as LinkAPI;
    const isEdit = api.getAlias() == Aliases.LINK_ADD_ITEM;
    const record = isEdit ? this.formItemEdit.getRecord() as unknown as Item : this.formItem.getRecord() as unknown as Item;
    const valid = isEdit ? this.formItemEdit.isValid() : this.formItem.isValid();

    if (!isEmptyArrayOrObject(record) && valid) {
      const value = isEdit ? [...this.itemListEdit.getValue()] : [...this.fieldItemList.getValue()];
      const dsItem = record.ieItemType == PROVIDER ?
        String(record.locatorProcedure.cdProcedure) + ' - ' + record.locatorProcedure.dsProcedure :
        String(record.medicineMaterialLocator.nrSeqMaterialLocator) + ' - ' + record.medicineMaterialLocator.dsMaterial;
      const item = {
        ieItemType: record.ieItemType,
        dsItem: dsItem,
        amount: record.amount,
        cdProcedure: record.locatorProcedure?.cdProcedure,
        nrSeqMaterial: record.medicineMaterialLocator?.nrSeqMaterialLocator,
        ieSourceProcedure: record.locatorProcedure?.ieSourceProcedure,
      };
      value.push(item);
      const reversed = [...value].reverse();
      if (isEdit) {
        this.itemListEdit.setValue(reversed);
        this.formItemEdit.setRecord({});
      } else {
        this.fieldItemList.setValue(reversed);
        this.formItem.setRecord({});
        this.wizardPackage.setStepDone('packageComposition', value.length > 0);
      }

    }
  }

  @OnSubmit(Aliases.FORM_EDIT_PACKAGE)
  onSubmitEditItem(event: SubmitEvent): void {
    const api = event.target as FormAPI;
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const record = api.getRecord() as unknown as Package;
    const { packageType, ieComposition } = record;
    let items;

    if (!packageType) {
      record.packageType = buttomWritePackage;

      if (ieComposition && ieComposition == HEALTH_INSURANCE) {
        record.packageType = buttomRequestPackage;
      }

    }

    if (record.packageType.key != HEALTH_INSURANCE) {
      items = this.itemListEdit.getValue();
    }

    if (record.packageType.key === PROVIDER) {
      const type = this.unboxedAutocompleteEdit.getValue();
      const formComposition = this.formItemEdit.getRecord() as unknown as Item;

      const radioGroupPackageTypeEditValue = this.radioGroupPackageTypeEdit.getValue()?.key;
      const isEmptyLocatorProcedure = isEmptyArrayOrObject(formComposition.locatorProcedure);
      const isEmptyMedicineMaterialLocator = isEmptyArrayOrObject(formComposition.medicineMaterialLocator);

      if (radioGroupPackageTypeEditValue === PROVIDER && type && (!isEmptyLocatorProcedure || !isEmptyMedicineMaterialLocator)) {
        const dsItem = type === PROCEDURE ? formComposition.locatorProcedure.dsProcedure : formComposition.medicineMaterialLocator.dsMaterial;
        const message = MessageService.translate('pa.package-item-warning').replace('#@DS_ITEM#@', dsItem);
        ToastServiceProviderAccreditation.error(message);
        return;
      }
    }

    const body = {
      vlPackage: record.vlPackage,
      composition: record.packageType.key,
      nrSeqPackage: record.nrSeqPackage,
      items,
    };

    HttpService.post<Record<string, Value>>(Endpoint.POST.PACKAGE_UPDATE, { nrSeqAccreditation }, { body })
      .then(() => {
        this.fetchPackages.forceFetch();
        this.panelEdit.setVisible(false);
      }).catch((response) => {
        ToastServiceProviderAccreditation.error(response.body);
      });

  }

  @OnAfterFetch(Aliases.FETCH_EDIT_PACKAGE)
  onAfterFetchEditPackage(event: FetchEvent): void {
    const data = event.data as Package;
    const { isDetails } = this.context.getBulkData() as unknown as Package;
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();

    if (isDetails) {
      this.formEditPackage.setEditing(false);
      this.formEditPackage.setDisabled(true);
      this.panelFormItemEdit.setVisible(false);
      this.panelLinkAddItems.setVisible(false);
      this.fieldiItemListEdit.setReadOnly(true);
      this.inputValue.setReadOnly(true);
      this.radioGroupPackageTypeEdit.setReadOnly(true);
    }

    if (data) {
      const { ieComposition, nrSeqPackage } = data;

      if (ieComposition == PROVIDER) {
        this.radioGroupPackageTypeEdit.setValue(buttomWritePackage);
        this.panelItemListEdit.setVisible(true);
        this.getPackageItems(nrSeqAccreditation as number, nrSeqPackage);
        return;
      }
      this.radioGroupPackageTypeEdit.setValue(buttomRequestPackage);
      this.panelTitle.setVisible(false);
    }
  }

  private deletePackage(nrSeqPackage: number): void {
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();

    HttpService.delete(Endpoint.DELETE.PACKAGE, { nrSeqAccreditation, nrSeqPackage })
      .then(() => {
        this.fetchPackages.setParameters({ nrSeqAccreditation });
        this.fetchPackages.forceFetch();
      }).catch((response) => {
        ToastServiceProviderAccreditation.error(response);
      });
  }

  private onDetails(nrSeqPackage: number, ieComposition: string): void {
    this.context.set({ nrSeqPackage, isDetails: true, ieComposition });
    this.panelEdit.setVisible(true);
  }

  private onEdit(nrSeqPackage: number, ieComposition: string): void {
    this.context.set({ nrSeqPackage, ieComposition, isDetails: false });
    this.panelEdit.setVisible(true);
  }

  private getPackageItems(nrSeqAccreditation: number, nrSeqPackage: number): void {
    this.panelLoader.setVisible(true);
    HttpService.get<Item[]>(Endpoint.GET.PACKAGE_ITEMS, { nrSeqAccreditation, nrSeqPackage })
      .then(({ body }) => {
        if (body) {
          this.itemListEdit.setValue(body as unknown as Record<string, Value>[]);
          this.panelLoader.setVisible(false);
        }
      }).catch((response) => {
        ToastServiceProviderAccreditation.error(response);
        this.panelLoader.setVisible(false);

      });
  }

}

export {
  ProviderAccreditationPackagesController,
};
